import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import ComparePlayer from "@/components/enhance/ComparePlayer";
import PolishPanel from "@/components/enhance/PolishPanel";
import ChangesList from "@/components/enhance/ChangesList";
import DownloadPanel from "@/components/enhance/DownloadPanel";
import SendToStudio from "@/components/enhance/SendToStudio";
import ProgressCard from "@/components/enhance/ProgressCard";
import useEnhancement from "@/components/enhance/useEnhancement";
import useCompare from "@/components/enhance/useCompare";
import { PHASES, fmtTime, retryEnhancement } from "@/lib/enhanceApi";
import { safeApiError } from "@/lib/safeErrorText";

function Facts({ item }) {
  const a = item.analysis || {};
  const facts = [["Length", fmtTime(item.info?.duration)], ["Key heard", a.key || "—"], ["Range", a.range || "—"],
    ["Background noise", a.noise_floor_db == null ? "—" : a.noise_floor_db > -48 ? "Noticeable" : a.noise_floor_db > -62 ? "A little" : "Very quiet"],
    ["Music removed", item.isolate ? "Yes" : "No"]];
  return (
    <div data-testid="enhance-facts" className="flex flex-wrap gap-2">
      {facts.map(([k, v]) => (
        <span key={k} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[12px]"><span className="text-white/45">{k}: </span>{v}</span>
      ))}
    </div>
  );
}

function Ready({ item, setItem }) {
  const cmp = useCompare(item);
  const [sending, setSending] = useState(false);
  const repolishing = item.status === "running";
  return (
    <div className="space-y-6">
      {repolishing ? (
        <div data-testid="enhance-repolishing" className="flex items-center gap-2 rounded-2xl border border-amber-300/25 bg-amber-300/10 px-4 py-3 text-[13px] text-amber-100">
          <Loader2 className="h-4 w-4 animate-spin" /> {PHASES[item.phase] || "Working"}… {item.versions?.polished ? "the new version replaces C when it's ready." : "Studio-Polished (C) appears here when it's ready."}
        </div>
      ) : null}
      <ComparePlayer item={item} cmp={cmp} />
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <PolishPanel item={item} onStarted={setItem} />
        <div className="space-y-6">
          <ChangesList item={item} version={cmp.active} />
          <button type="button" data-testid="enhance-send-open" onClick={() => { cmp.pause(); setSending(true); }}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-amber-300/40 bg-amber-300/10 py-3 text-[13px] font-semibold text-amber-100 transition-colors hover:bg-amber-300/20">
            <Send className="h-4 w-4" /> Send to a Recording Studio track
          </button>
          <DownloadPanel item={item} />
        </div>
      </div>
      <SendToStudio open={sending} onClose={() => setSending(false)} item={item} version={cmp.active} />
    </div>
  );
}

/** `/create/enhance/:id` — progress, then the A/B/C compare, polish controls, downloads and send to studio. */
export default function EnhanceSessionPage() {
  const { id } = useParams();
  const { item, setItem, error } = useEnhancement(id);
  const [retrying, setRetrying] = useState(false);
  const retry = async () => {
    setRetrying(true);
    try { setItem(await retryEnhancement(id)); } catch (e) { toast.error("Couldn't restart", { description: safeApiError(e) }); }
    finally { setRetrying(false); }
  };
  const hasVersions = Boolean(item?.versions?.natural);
  return (
    <CreatorShell testId="enhance-session-page" back="/create/enhance" title={item?.title || "Enhance My Vocals"} subtitle="Original · Natural Voice · Studio-Polished">
      {error && !item ? <p data-testid="enhance-not-found" className="text-[14px] text-rose-200">{safeApiError(error)}</p> : null}
      {!item && !error ? <Loader2 className="h-6 w-6 animate-spin text-white/40" /> : null}
      {item ? (
        <div className="space-y-6">
          <Facts item={item} />
          {item.status === "error" || !hasVersions ? <ProgressCard item={item} onRetry={retry} retrying={retrying} /> : null}
          {hasVersions ? <Ready item={item} setItem={setItem} /> : null}
        </div>
      ) : null}
    </CreatorShell>
  );
}
